import { canManageOrganization } from 'util/organizationAccess';
import React, { useState } from 'react';
import { Badge, Button, Nav } from 'react-bootstrap';
import useShareRequestTabs from 'hooks/useShareRequestTabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import corner6 from 'assets/img/illustrations/corner-6.png';
import FalconComponentCard from 'components/common/FalconComponentCard';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import AssociationRequestModal from 'components/common/AssociationRequestModal';
import AdminNotariesShareTab from './AdminNotariesShareTab';
import {
  SHARE_REQUEST_TAGS,
  useCreateShareRequestMutation,
  shareRequestApi
} from 'rtk/org-admin/share-request.slice';

const AdminNotariesConnection = () => {
  const canManage = useSelector(state =>
    canManageOrganization(state.authentication?.userRoles)
  );
  const dispatch = useDispatch();
  const { activeTab, selectTab, sentCount, receivedCount, targetGuid } =
    useShareRequestTabs();
  const [showSendModal, setShowSendModal] = useState(false);
  const [createShareRequest, { isLoading: sending }] =
    useCreateShareRequestMutation();

  const handleRefresh = () => {
    dispatch(
      shareRequestApi.util.invalidateTags([
        { type: SHARE_REQUEST_TAGS.SHARE_REQUEST, id: 'LIST' }
      ])
    );
  };

  const statusOptions = [
    'PENDING',
    'REJECTED',
    'EXPIRED',
    'CANCELLED',
    'ACCEPTED',
    'ALL'
  ];

  const handleSendConnectionRequest = () => {
    setShowSendModal(true);
  };

  const handleSendRequest = async ({ email, locationGuid }) => {
    try {
      const res = await createShareRequest({
        ToEmail: email.trim(),
        ...(locationGuid ? { LocationGuid: locationGuid } : {})
      }).unwrap();
      toast.success(
        res?.InviteSent
          ? 'Signup invitation sent. The connection request will be available after the notary creates an account.'
          : 'Connection request sent.'
      );
      setShowSendModal(false);
    } catch (err) {
      if (!err) return;
      toast.error(_.get(err, 'Text', 'Failed to send connection request.'));
      throw err;
    }
  };

  return (
    <>
      {/* <PageHeader
        title="Notary Account Management"
        description="Manage associated notary accounts and send connection requests."
        titleTag="h2"
        className="mb-3"
        image={corner6}
        col={{ lg: 8, md: 7, sm: 12 }}
      /> */}

      <FalconComponentCard noGuttersBottom>
        <FalconComponentCard.Header
          image={corner6}
          className={'px-4'}
          title="Notary Share Requests"
          subtitle="Manage associated notary accounts and connection requests"
          endEl={
            canManage && (
              <Button variant="primary" onClick={handleSendConnectionRequest}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Send Connection Request
              </Button>
            )
          }
        />
        <FalconComponentCard.Body noLight className="py-0 px-4 fs--1">
          <Nav
            variant="tabs"
            activeKey={activeTab}
            onSelect={selectTab}
            className="mb-3"
          >
            <Nav.Item>
              <Nav.Link eventKey="sent">
                Sent{' '}
                {sentCount > 0 && (
                  <Badge bg="secondary" title="Pending sent requests">
                    {sentCount}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="received">
                Received{' '}
                {receivedCount > 0 && (
                  <Badge bg="primary" title="Pending received requests">
                    {receivedCount}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {activeTab === 'sent' && (
            <AdminNotariesShareTab
              key={`sent:${targetGuid || ''}`}
              direction="SENT"
              statusOptions={statusOptions}
            />
          )}
          {activeTab === 'received' && (
            <AdminNotariesShareTab
              key={`received:${targetGuid || ''}`}
              direction="RECEIVED"
              statusOptions={statusOptions}
            />
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>

      <AssociationRequestModal
        show={showSendModal}
        onHide={() => setShowSendModal(false)}
        title="Send Connection Request"
        emailLabel="Notary email address"
        emailPlaceholder="email@example.com"
        showLocationDropdown={true}
        locationLabel="Location"
        showAccessType={false}
        sending={sending}
        onSubmit={handleSendRequest}
        cancelLabel="Cancel"
        submitLabel="Send Request"
      />
    </>
  );
};

export default AdminNotariesConnection;
